// src/components/common/textarea/Textarea.tsx
"use client";

import React, { forwardRef, TextareaHTMLAttributes, useMemo } from "react";
import { useField } from "formik";
import FieldError from "../field-error/FieldError";
import { isRTLText } from "../../../utils/helpers/languageHelpers";
import "./Textarea.css";

interface TextareaProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label?: string;
  name: string;
  maxLength?: number;
  error?: string;
  showCounter?: boolean;
}

const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea(
  { label, name, maxLength, error, showCounter = true, ...rest },
  ref
) {
  const [field, meta] = useField(name);

  const value = field.value || "";
  const currentLength = value.length;
  const hasError = meta.touched && meta.error;

  // Detect text direction dynamically
  const isRTL = useMemo(() => isRTLText(value), [value]);

  return (
    <div className="textarea-wrapper">
      {label && (
        <label htmlFor={name} className="textarea-label">
          {label}
        </label>
      )}

      <div className="textarea-inner">
        <textarea
          id={name}
          ref={ref}
          {...field}
          {...rest}
          maxLength={maxLength}
          dir={isRTL ? "rtl" : "ltr"}
          className={`textarea-field ${hasError ? "has-error" : ""}`}
        />

        {showCounter && maxLength && (
          <span className="textarea-counter">
            {currentLength}/{maxLength}
          </span>
        )}
      </div>

      {hasError && <FieldError>{meta.error}</FieldError>}
    </div>
  );
});

export default Textarea;
